// src/features/documents/hooks/useAllDocumentsReady.ts
import {useAppSelector} from "../redux/hooks";
import {
    selectAreAllDocumentsDetailsLoaded,
    selectDocumentsError,
    selectDocumentsLoading, selectDocumentsLoadProgress
} from "../redux/api/documents/documentsSelectors";

/** Результат работы useAllDocumentsReady */
export interface UseAllDocumentsReadyResult {
    /** Флаг, означающий, что процесс загрузки всех документов ещё не завершён. */
    isLoading: boolean;
    /** Прогресс (0..100) из селектора selectDocumentsLoadProgress. */
    progress: number;
    /** Ошибка загрузки (если была). */
    error: string | null;
}

/**
 * Хук, который возвращает информацию о загрузке ВСЕХ документов (detailLoaded):
 *  - isLoading = true, пока не все detailLoaded
 *  - progress = процент загрузки (0..100)
 *  - error, если была при загрузке
 *
 * Не вызывает никаких dispatch, так как загрузка происходит в AppInitializer + listenerMiddleware.
 */
export function useAllDocumentsReady(): UseAllDocumentsReadyResult {
    const areAllLoaded = useAppSelector(selectAreAllDocumentsDetailsLoaded);
    const isGlobalLoading = useAppSelector(selectDocumentsLoading);
    const documentsError = useAppSelector(selectDocumentsError);
    const loadProgress = useAppSelector(selectDocumentsLoadProgress);

    // Если при загрузке произошла ошибка, храним её в error
    const error = documentsError || null;

    // Будем считать, что isLoading = true, пока
    // 1) global loading флаг не сброшен
    //    ИЛИ
    // 2) не все документы успели загрузиться (areAllLoaded=false)
    const isLoading = isGlobalLoading || !areAllLoaded;

    // Получаем процент "прогресса" из селектора (loaded / total * 100)
    const { loaded, total } = loadProgress;
    // Если total=0 (нет документов), пусть будет 0
    const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;

    return {
        isLoading,
        progress,
        error
    };
}
